import { readableDate, Storage } from '@chocolate-soup-inc/cs-api-consumer-utils';
import { Card, Chip, ConfirmationModal, CountryAvatar, FloatingMenu, getCountryName, Icon, IconButton, TConfirmationModalProps, TMenuItemProps, Tooltip } from '@chocolate-soup-inc/cs-frontend-components';
import clsx from 'clsx';
import _ from 'lodash';
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { serializeError } from 'serialize-error';
import { TDraggableCardRenderOptions } from '../../../components/board/DraggableCard';
import { CANADAPOST_SERVICE_OPTIONS, CARRIER_OPTIONS, DHL_SERVICE_OPTIONS, generateCUSMAformPDF, PUROLATOR_SERVICE_OPTIONS, UPS_SERVICE_OPTIONS, useShipmentAddressInfo } from '../../../entities/shipments/shared';
import {
  TAlert,
  TCarriers,
  TShipmentStatuses,
  TShipStationAddressVerified,
  useDeleteShipmentMutation,
  usePrintLabelPhysicallyMutation,
  useRecreateShipmentMutation,
  useResyncShipmentWithShipStationMutation,
  useSetShipmentForPickupMutation
} from '../../../generated/graphql';
import { SHIPMENT_PATH } from '../../../routes/paths';

import { AlertsTooltip } from '../../../components/alerts/AlertsTooltip';
import { isAddressValid } from '../../../entities/addresses/shared';
import { TShipmentType } from '../../../entities/shipments/queries';
import styles from './ShipmentCard.module.scss';

type TShipmentCardProps = {
  shipment: TShipmentType;
  options?: TDraggableCardRenderOptions & {
    hoverDisabled?: boolean;
  };
  onShipManually?: () => void;
  onGroupable?: () => void;
  onCarrierOverride?: () => void;
};

type TShipmentCardDisplayData = {
  addressTypeIcon?: string;
  companyName?: string;
  country?: string;
  readableAddressType?: string;
  recipientName?: string;
};

const shipmentDisplayDataCache = new Map<string, TShipmentCardDisplayData>();

export const ShipmentCard = (props: TShipmentCardProps) => {
  const { shipment, options: { hoverDisabled = false, isSelected, isDragging, measureElement, overlay, rowIndexAttribute, rowIndexAttributeName } = {}, onShipManually, onGroupable, onCarrierOverride } = props;

  const [confirmationProps, setConfirmationProps] = useState<Omit<TConfirmationModalProps, 'closeModal' | 'confirmLoading' | 'onCancelClick'>>();

  let addressTypeIcon, readableAddressType, recipientName;

  const { addressTypeIcon: oldAddressTypeIcon, readableAddressType: oldReadableAddressType, recipientName: oldRecipíentName } = useShipmentAddressInfo(shipment);

  if (shipment.addressTypeIcon) addressTypeIcon = shipment.addressTypeIcon;
  else addressTypeIcon = oldAddressTypeIcon;

  if (shipment.readableAddressType) readableAddressType = shipment.readableAddressType;
  else readableAddressType = oldReadableAddressType;

  if (shipment.recipientName) recipientName = shipment.recipientName;
  else recipientName = oldRecipíentName;

  const previousDisplayDataRef = useRef<TShipmentCardDisplayData>(shipmentDisplayDataCache.get(shipment.id) || {});

  const displayData = useMemo(
    () => ({
      addressTypeIcon: addressTypeIcon || previousDisplayDataRef.current.addressTypeIcon,
      companyName: shipment.company?.name || previousDisplayDataRef.current.companyName,
      country: shipment.address?.country || previousDisplayDataRef.current.country,
      readableAddressType: readableAddressType || previousDisplayDataRef.current.readableAddressType,
      recipientName: recipientName || previousDisplayDataRef.current.recipientName
    }),
    [addressTypeIcon, readableAddressType, recipientName, shipment.address?.country, shipment.company?.name]
  );

  useEffect(() => {
    previousDisplayDataRef.current = {
      addressTypeIcon: displayData.addressTypeIcon,
      companyName: displayData.companyName,
      country: displayData.country,
      readableAddressType: displayData.readableAddressType,
      recipientName: displayData.recipientName
    };
    shipmentDisplayDataCache.set(shipment.id, previousDisplayDataRef.current);
  }, [displayData, shipment.id]);

  const [openLabelLoading, setOpenLabelLoading] = useState<boolean>(false);

  const labelDataPresent = useMemo(() => {
    return shipment.label?.labelFile && shipment.label.labelFile.key && shipment.label.labelFile.bucket && shipment.label.labelFile.region;
  }, [shipment]);

  const addressVerified = useMemo(() => {
    return shipment.addressVerified;
  }, [shipment.addressVerified]);

  const [printLabelPhysically] = usePrintLabelPhysicallyMutation({});

  const openLabel = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (labelDataPresent) {
        setOpenLabelLoading(true);

        printLabelPhysically({
          variables: { companyId: shipment.companyId, id: shipment.id, version: shipment._version }
        })
          .then()
          .catch(() => toast.error('Error updating printing status'));

        Storage.get(shipment.label?.labelFile?.key as string, {
          bucket: shipment.label?.labelFile?.bucket as string,
          region: shipment.label?.labelFile?.region as string
        })
          .then((url) => {
            window.open(url, '_blank');
          })
          .catch((error) => {
            console.error(serializeError(error));
            toast.error('There was an error retrieving the label.');
          })
          .then(() => {
            setOpenLabelLoading(false);
          });
      }
    },
    [labelDataPresent, shipment, printLabelPhysically]
  );

  const [resyncShipmentWithShipStation, { loading: resyncWithShipStationLoading }] = useResyncShipmentWithShipStationMutation();

  const syncWithShipStation = useCallback(
    (e: MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      if (shipment && [TShipmentStatuses.ReadyToShip, TShipmentStatuses.PrintingLabel].includes(shipment.status))
        resyncShipmentWithShipStation({
          variables: {
            id: shipment.id,
            companyId: shipment.companyId,
            version: shipment._version
          }
        })
          .then(() => {
            toast.success('Shipment set for re-sync with ShipStation successfully. It might take a couple of minutes for resync to be done.');
          })
          .catch((error) => {
            console.error(serializeError(error));
            toast.error('There was an error resyncing the shipment with ShipStation.');
          });
    },
    [resyncShipmentWithShipStation, shipment]
  );

  const [menuOpen, setMenuOpen] = useState<boolean>(false);

  const [setShipmentForPickup, { loading: setShipmentForPickupLoading }] = useSetShipmentForPickupMutation({
    variables: {
      id: shipment.id,
      companyId: shipment.companyId,
      version: shipment._version
    }
  });

  const onActualSetShipmentForPickup = useCallback(() => {
    setShipmentForPickup()
      .then()
      .catch((error) => {
        console.error(serializeError(error));
        toast.error('There was an error setting the shipment for pickup.');
      })
      .then(() => {
        setConfirmationProps(undefined);
      });
  }, [setShipmentForPickup]);

  const onSetForPickupClick = useCallback(() => {
    setConfirmationProps({
      headline: 'Set shipment for Pickup?',
      supportingText: 'Are you sure you want to set this shipment for pickup?',
      confirmLabel: 'Set for pickup',
      onConfirmClick: onActualSetShipmentForPickup
    });
  }, [onActualSetShipmentForPickup]);

  const [deleteShipment, { loading: deleteLoading }] = useDeleteShipmentMutation({
    variables: {
      id: shipment.id,
      companyId: shipment.companyId,
      version: shipment._version
    }
  });

  const onActualDelete = useCallback(() => {
    deleteShipment()
      .then()
      .catch((error) => {
        console.error(serializeError(error));
        toast.error('There was an error deleting the shipment.');
      })
      .then(() => {
        setConfirmationProps(undefined);
      });
  }, [deleteShipment]);

  const onDeleteClick = useCallback(() => {
    setConfirmationProps({
      headline: 'Delete shipment?',
      supportingText: 'Are you sure you want to delete this shipment?',
      confirmLabel: 'Delete',
      onConfirmClick: onActualDelete
    });
  }, [onActualDelete]);

  const [recreateShipment, { loading: recreateLoading }] = useRecreateShipmentMutation({
    variables: {
      id: shipment.id,
      companyId: shipment.companyId
    }
  });

  const onActualRecreate = useCallback(() => {
    recreateShipment()
      .then()
      .catch((error) => {
        console.error(serializeError(error));
        toast.error('There was an error recreating the shipment.');
      })
      .then(() => {
        setConfirmationProps(undefined);
      });
  }, [recreateShipment]);

  const onRecreateClick = useCallback(() => {
    setConfirmationProps({
      headline: 'Recreate shipment?',
      supportingText: 'Are you sure you want to recreate this shipment gifts?',
      confirmLabel: 'Recreate',
      onConfirmClick: onActualRecreate
    });
  }, [onActualRecreate]);

  const menuOptions: TMenuItemProps[] = useMemo(() => {
    const opts: TMenuItemProps[] = [];

    if (shipment.status === TShipmentStatuses.Packaging) {
      opts.push({
        disabled: deleteLoading || recreateLoading,
        label: 'Override Carrier',
        leadingIcon: 'trolley',
        loading: setShipmentForPickupLoading,
        type: 'text',
        onClick: () => {
          if (onCarrierOverride) onCarrierOverride();
          setMenuOpen(false);
        }
      });

      opts.push({
        disabled: deleteLoading || recreateLoading,
        label: 'Set for Pickup',
        leadingIcon: 'home_work',
        loading: setShipmentForPickupLoading,
        type: 'text',
        onClick: onSetForPickupClick
      });

      if (shipment.groupable)
        opts.push({
          label: 'Group by Address',
          leadingIcon: 'view_compact_alt',
          type: 'text',
          onClick: () => {
            if (onGroupable) onGroupable();
            setMenuOpen(false);
          }
        });
    }

    if (
      [
        //TShipmentStatuses.ReadyToShip,
        //TShipmentStatuses.PrintingLabel,
        TShipmentStatuses.LabelPrinted,
        TShipmentStatuses.Shipped
      ].includes(shipment.status) &&
      ['usa'].includes(shipment.address?.country?.toLowerCase() as string)
    )
      opts.push({
        label: 'Print CUSMA (USA) form (UPS)',
        leadingIcon: 'file_save',
        loading: recreateLoading,
        type: 'text',
        onClick: () => {
          generateCUSMAformPDF(shipment);
        }
      });

    if ([TShipmentStatuses.PrintingLabel, TShipmentStatuses.LabelPrinted, TShipmentStatuses.Shipped, TShipmentStatuses.Attention, TShipmentStatuses.Delivered, TShipmentStatuses.Error, TShipmentStatuses.Canceled].includes(shipment.status))
      opts.push({
        disabled: deleteLoading || setShipmentForPickupLoading,
        label: 'Recreate Shipment',
        leadingIcon: 'control_point_duplicate',
        loading: recreateLoading,
        type: 'text',
        onClick: onRecreateClick
      });

    if (shipment.trackingNumber)
      opts.push({
        disabled: deleteLoading || recreateLoading || setShipmentForPickupLoading,
        label: 'Copy Tracking Number',
        leadingIcon: 'content_copy',
        type: 'text',
        onClick: () => {
          if (shipment.trackingNumber) {
            setMenuOpen(false);
            navigator.clipboard.writeText(shipment.trackingNumber);
            toast.success('Tracking Number Copied to Clipboard');
          }
        }
      });

    if (shipment.trackingURL)
      opts.push({
        disabled: deleteLoading || recreateLoading || setShipmentForPickupLoading,
        label: 'Track Shipment',
        leadingIcon: 'open_in_new',
        type: 'text',
        onClick: () => {
          if (shipment.trackingURL) {
            setMenuOpen(false);
            window.open(shipment.trackingURL, '_blank');
          }
        }
      });

    if ([TShipmentStatuses.ReadyToShip, TShipmentStatuses.PrintingLabel].includes(shipment.status))
      opts.push({
        label: 'Ship Manually',
        leadingIcon: 'edit',
        loading: deleteLoading,
        onClick: () => {
          if (onShipManually) onShipManually();
          setMenuOpen(false);
        },
        type: 'text'
      });

    if ([TShipmentStatuses.Packaging, TShipmentStatuses.ReadyToShip, TShipmentStatuses.LabelPrinted, TShipmentStatuses.PrintingLabel, TShipmentStatuses.ReadyForPickup].includes(shipment.status))
      opts.push({
        disabled: setShipmentForPickupLoading || recreateLoading,
        label: 'Delete Shipment',
        leadingIcon: 'delete',
        loading: deleteLoading,
        onClick: onDeleteClick,
        type: 'text'
      });

    return opts;
  }, [deleteLoading, onDeleteClick, onRecreateClick, onSetForPickupClick, recreateLoading, setShipmentForPickupLoading, shipment, onShipManually, onGroupable, onCarrierOverride]);

  const onMoreClick = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setMenuOpen((open) => !open);
  }, []);

  const navigate = useNavigate();

  const onCardClick = useCallback(
    (e: MouseEvent<HTMLDivElement>) => {
      if (!e.shiftKey && !e.ctrlKey && !e.metaKey) navigate(generatePath(`${SHIPMENT_PATH}`, { shipmentId: shipment.id }));
    },
    [navigate, shipment.id]
  );

  const countryName = useMemo(() => {
    return getCountryName(shipment.address?.country || undefined);
  }, [shipment]);

  const shipmentHistory = useMemo(() => {
    return shipment.history || [];
  }, [shipment.history]);

  const isReship = useMemo(() => {
    return shipmentHistory.length > 0;
  }, [shipmentHistory]);

  const employeeIsDeleted = useMemo(() => {
    return shipment.addressId.includes(import.meta.env.REACT_APP_EMPLOYEES_TABLE_NAME as string) && !shipment.employee;
  }, [shipment]);

  const allShipmentAlertsHaveBeenDismissed = useMemo(() => {
    if (!shipment.alerts) return true;
    return shipment.alerts.every((a) => {
      return !!a?.acknowledgedAt;
    });
  }, [shipment.alerts]);

  const isPrintedPhysically = useMemo(() => shipment.isPrintedPhysically && shipment.status == TShipmentStatuses.LabelPrinted, [shipment]);

  const hasGifts = useMemo(() => shipment.gifts?.items && shipment.gifts.items.length, [shipment]);

  const giftsAmount: number = useMemo(() => {
    if (shipment.gifts?.items) return shipment.gifts.items.length ?? 0;
    //if (shipment.gifts) return shipment.gifts.length ?? 0;
    return 0;
  }, [shipment]);

  const overrideCarrier = useMemo(() => {
    if (!shipment.overrideCarrier || !shipment.overrideService) return null;
    let overrideTxt = '';
    if (shipment.overrideCarrier) overrideTxt = overrideTxt + CARRIER_OPTIONS[shipment.overrideCarrier];
    if (shipment.overrideService)
      switch (shipment.overrideCarrier) {
        case TCarriers.CanadaPost:
          overrideTxt = overrideTxt + ' ' + CANADAPOST_SERVICE_OPTIONS[shipment.overrideService];
          break;
        case TCarriers.Ups:
          overrideTxt = overrideTxt + ' ' + UPS_SERVICE_OPTIONS[shipment.overrideService];
          break;
        case TCarriers.PurolatorCa:
          overrideTxt = overrideTxt + ' ' + PUROLATOR_SERVICE_OPTIONS[shipment.overrideService];
          break;
        case TCarriers.DhlExpressCanada:
          overrideTxt = overrideTxt + ' ' + DHL_SERVICE_OPTIONS[shipment.overrideService];
          break;
      }

    return overrideTxt;
  }, [shipment]);

  const missingAddress = useMemo(() => {
    return shipment.addressId == null || !isAddressValid(shipment.address);
  }, [shipment.addressId, shipment.address]);

  return (
    <>
      {!_.isEmpty(confirmationProps) && (
        <ConfirmationModal {...confirmationProps} closeModal={() => setConfirmationProps(undefined)} confirmLoading={deleteLoading || setShipmentForPickupLoading || recreateLoading} onCancelClick={() => setConfirmationProps(undefined)} />
      )}
      <div ref={measureElement} {...(rowIndexAttributeName ? { [rowIndexAttributeName]: rowIndexAttribute } : {})} onClick={onCardClick}>
        <Card
          className={clsx(
            styles.card,
            (!allShipmentAlertsHaveBeenDismissed || isReship || !displayData.recipientName) && styles.alert,
            employeeIsDeleted && styles.deletedEmployee,
            isPrintedPhysically && styles.isPrintedPhysically,
            shipment.groupable && styles.groupable
          )}
          readOnly={hoverDisabled || overlay}
          variant={isSelected ? 'elevated' : 'outlined'}>
          <>
            <div className={styles.icons}>
              <div className={styles.options}>
                <div className={styles.alertIcons}>
                  {shipment.isPrintedCUSMA && (
                    <Tooltip message={'CUSMA (USA) form (UPS) printed'}>
                      <Icon icon='print' className={styles.addressVerifiedSuccess} />
                    </Tooltip>
                  )}
                  {shipment.notes && (
                    <Tooltip message={shipment.notes}>
                      <Icon icon='comment' />
                    </Tooltip>
                  )}
                  {shipment.label?.isThirdParty && (
                    <Tooltip message='Third party billed shipment.'>
                      <Icon className={styles.addressVerifiedSuccess} icon='output' />
                    </Tooltip>
                  )}
                  <AlertsTooltip alerts={shipment.alerts as TAlert[]} />
                  {shipment.groupable && (
                    <Tooltip message='This shipment can be combined.'>
                      <Icon className={styles.addressVerifiedSuccess} icon='view_compact_alt' />
                    </Tooltip>
                  )}
                  {addressVerified === TShipStationAddressVerified.Success && (
                    <Tooltip message='ShipStation address verification succeeded.'>
                      <Icon className={styles.addressVerifiedSuccess} icon='check_circle' />
                    </Tooltip>
                  )}
                  {addressVerified === TShipStationAddressVerified.Failed && (
                    <Tooltip message='ShipStation address verification failed.'>
                      <Icon className={styles.addressVerifiedFail} icon='error_circle_rounded' />
                    </Tooltip>
                  )}
                  {addressVerified === TShipStationAddressVerified.Warning && (
                    <Tooltip message='ShipStation address verification warning.'>
                      <Icon className={styles.addressVerifiedWarning} icon='warning' />
                    </Tooltip>
                  )}
                  {missingAddress && (
                    <Tooltip message='Missing address data.'>
                      <Icon icon='not_listed_location' />
                    </Tooltip>
                  )}
                  {employeeIsDeleted && (
                    <Tooltip message='Employee Deleted.'>
                      <Icon icon='person_off' />
                    </Tooltip>
                  )}
                </div>
                {[TShipmentStatuses.ReadyToShip, TShipmentStatuses.PrintingLabel].includes(shipment.status) && (
                  <Tooltip message={`Resync shipment with ShipStation.${shipment.status === TShipmentStatuses.PrintingLabel ? ` This will also try to print the label again.` : ''}`}>
                    <IconButton disabled={resyncWithShipStationLoading} icon='sync' onClick={syncWithShipStation} variant='standard' />
                  </Tooltip>
                )}
                {labelDataPresent && <IconButton disabled={openLabelLoading} icon='print' onClick={openLabel} variant='standard' />}
                <FloatingMenu autoPlacement={false} placement='bottom-end' open={menuOpen} onOpenChange={setMenuOpen} options={menuOptions}>
                  <IconButton icon='more_vert' onClick={onMoreClick} variant='standard' />
                </FloatingMenu>
              </div>
            </div>
            <div className={styles.country}>
              {displayData.readableAddressType && displayData.addressTypeIcon && displayData.country && countryName && (
                <Tooltip autoPlacement={false} message={countryName} placement='bottom'>
                  <CountryAvatar elevated={true} size='medium' code={displayData.country} />
                </Tooltip>
              )}
            </div>
            <span className={styles.companyName}>{displayData.companyName}</span>
            <span className={styles.recipientName}>{displayData.recipientName}</span>
            <div className={styles.tags}>
              <Tooltip autoPlacement={false} disabled={isDragging} message='Delivery Date' placement='bottom'>
                <Chip
                  innerContainerClassName={styles.chip}
                  leadingIcon='local_shipping'
                  leadingIconProps={{
                    filled: true
                  }}
                  label={readableDate(shipment.actualShippingDate || shipment.shippingDate, 'dd/MM/yyyy')}
                  readonly={true}
                  variant='input'
                />
              </Tooltip>
              <Tooltip autoPlacement={false} disabled={isDragging} message='Address Type' placement='bottom'>
                <Chip
                  innerContainerClassName={styles.chip}
                  leadingIcon={displayData.addressTypeIcon}
                  leadingIconProps={{
                    filled: true
                  }}
                  label={displayData.readableAddressType || 'unknown'}
                  readonly={true}
                  variant='input'
                />
              </Tooltip>
              {isReship && (
                <Tooltip autoPlacement={false} disabled={isDragging} message='This is a Reship of a returned shipment' placement='bottom'>
                  <Chip
                    innerContainerClassName={styles.chip}
                    leadingIcon='replay'
                    leadingIconProps={{
                      weight: 500
                    }}
                    label='Reship'
                    readonly={true}
                    variant='input'
                  />
                </Tooltip>
              )}
              {hasGifts && (
                <Tooltip autoPlacement={false} disabled={isDragging} message='Gift Count' placement='bottom'>
                  <Chip innerContainerClassName={styles.chip} leadingIcon='card_giftcard' label={`${giftsAmount} Gift${giftsAmount > 1 ? 's' : ''}`} readonly={true} variant='input' />
                </Tooltip>
              )}
              {overrideCarrier && (
                <Tooltip autoPlacement={false} disabled={isDragging} message='Carrier Override' placement='bottom'>
                  <Chip innerContainerClassName={styles.chip} leadingIcon='trolley' label={overrideCarrier} readonly={true} variant='input' />
                </Tooltip>
              )}
            </div>
          </>
        </Card>
      </div>
    </>
  );
};
